import { Alert } from '@mui/material';
import * as Sentry from '@sentry/react';
import Button from '_atoms/Button/Button';
import FormControl from '_atoms/Form/Control';
import InputBox from '_atoms/InputBox';
import User from '_services/Api/User';
import { LoginData } from '_services/Api/User/type';
import LocalStorageCoupon from '_services/LocalStorage/Coupon/LocalStorageCoupon';
import RedirectAfterLogin from '_services/LocalStorage/RedirectAfterLogin';
import { userSlice } from '_services/Redux/User/UserSlice';
import { HasFieldValueInArrayOfObjects } from '_utils/HasFieldValueInArray';
import useCheckoutAfterLogin from 'Hooks/useCheckoutAfterLogin';
import useNavigate from 'Hooks/useNavigate';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import './login.scss';
import { LoginErrors } from './type';

/**
 * The entire Login component. Contains:
 * - login form + validation
 * - API call on submit if frontend validation is passed
 * - link to registration in case the user doesn't have an account
 *
 * @returns JSX.Element
 */
type PropsType = { email?: string; isSmallAndroidDevice?: boolean };

const LoginSection: FC<PropsType> = ({
    email,
    isSmallAndroidDevice = false,
}): React.JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loginUser } = userSlice.actions;

    const [handleLoggedIn] = useCheckoutAfterLogin();

    /**
     * The state of this functional component is used to store the email and password from the form.
     * These are passed further to the post request for login
     */
    const [state, setState] = useState<LoginData>({
        email: email ?? '',
        password: '',
    });

    useEffect(() => {
        if (email) {
            setState((prevState) => ({ ...prevState, email }));
        }
    }, [email]);
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Holds the errors of the form or request.
     * These are populated with inner validation or api request errors
     *
     * @see isValid()
     * @see handleSubmit()
     */
    const [errors, setErrors] = useState<LoginErrors>({});

    /**
     * Checks whether the login form is valid. Sets errors to the state if it's not.
     *
     * @returns boolean
     */
    const isValid = () => {
        /**
         * Contains the validation of the email and password inputs + error messages
         */
        const errors: LoginErrors = {
            email: state.email
                ? ''
                : t('Email') + '' + t('is a required field'),
            password: state.password
                ? ''
                : t('Password') + '' + t('is a required field'),
        };

        if (state.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
            errors.email =
                `"${state.email}" ` + t('is not a valid email address');
        }

        /**
         * Checks whether the form is valid by confirming that each propriety in the errors object doesn't contain any error message
         */
        const errorValues = Object.values(errors);
        const valid = errorValues.every((error) => error === '');
        if (!valid) {
            /**
             * Updates the state with validation errors if there are any
             */
            setErrors({ error: errorValues[0] });
        }
        return valid;
    };

    /**
     * Handles the submit of the form. If internal validation passes, the submit is furthered to the API
     *
     * @param event React.FormEvent<HTMLFormElement>
     * @returns void
     */
    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (!isValid()) {
            /**
             * Stops further execution if the inputs are not valid
             */
            return;
        }
        setIsLoading(true);
        /**
         * Using the state as post data.
         * @see state
         */
        const loginPostData = state;

        /**
         * Login is called on the api if cookie is received
         */
        User.login(loginPostData)
            /**
             * When api call is done, use login action that dispatches the user to redux
             */
            .then((response) => {
                const userData = response.data.user,
                    userCoupons = userData.coupons,
                    locallyStoredCoupon = LocalStorageCoupon.get();

                if (
                    locallyStoredCoupon &&
                    userCoupons instanceof Array &&
                    HasFieldValueInArrayOfObjects(
                        'code',
                        locallyStoredCoupon,
                        userCoupons,
                    )
                ) {
                    /**
                     * If response user data has coupon code,
                     * and it is the same as the coupon from the LocalStorage,
                     * then we can remove the coupon from LocalStorage,
                     * because it means it is now saved in the API
                     */
                    LocalStorageCoupon.remove();
                }
                const urlToRedirectAfterLogin = RedirectAfterLogin.get();

                if (urlToRedirectAfterLogin) {
                    RedirectAfterLogin.remove();
                    window.location.href = decodeURI(urlToRedirectAfterLogin);
                }

                Sentry.withScope((scope) => {
                    scope.setTag('login_method', 'password_fallback');
                    scope.setLevel('info');
                    scope.setContext('login', {
                        route: 'LoginWithPasswordScreen',
                        hasRedirect: Boolean(urlToRedirectAfterLogin),
                    });
                    Sentry.captureMessage('password_fallback_login_success');
                });

                dispatch(loginUser(userData));
                handleLoggedIn({ user: userData, setIsLoading });
            })
            /**
             * If any errors happen during login, they should be handled here
             */
            .catch((error) => {
                setIsLoading(false);

                const { message } = error?.response?.data ?? {};
                if (error.response?.status === 422) {
                    setErrors(error.response.data);
                    return;
                }

                if (message === 'CSRF token mismatch.') {
                    setErrors({
                        error: 'Token expired, Please wait we are clearing it',
                    });

                    User.clearCookies()
                        .then(() => {
                            const loginFormElement = document.getElementById(
                                'login-btn',
                            ) as HTMLButtonElement;

                            if (loginFormElement) {
                                loginFormElement.click();
                            }
                        })
                        .catch(() => {
                            setErrors({
                                error: 'Unable to clear cookies, Please re-install the app or contact us',
                            });
                        });

                    return;
                }

                if (message) {
                    setErrors({ error: message });
                } else {
                    /**
                     * If it ends up here, then we don't really know what happened
                     */
                    setErrors({ error: 'An unexpected error happened.' });

                    Sentry.captureException(error);
                }
            });
    }

    /**
     * Handles all changes in the form by Input target.
     * Updates the state with propriety of the input id and the value from the input value
     *
     * @param event React.ChangeEvent<HTMLInputElement>
     */
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;

        if (event.target.id === 'email') {
            value = value.replace(/\s/g, '');
        }

        setState({ ...state, [event.target.id]: value });
    };

    const isSubmittable = (): boolean => {
        if (!state.email || !state.password) {
            return false;
        }

        return true;
    };

    /**
     * Finally we return the form used for login
     */
    return (
        <>
            <form onSubmit={handleSubmit} className="login-form">
                {errors.error && (
                    <Alert className="form-control" severity="error">
                        {errors.error}
                    </Alert>
                )}
                <FormControl>
                    <InputBox
                        value={state.email}
                        type="email"
                        id="email"
                        label="Email"
                        handleChange={handleChange}
                        placeholder="Email"
                        testId="email"
                        handleFocus={() => {
                            if (isSmallAndroidDevice) {
                                setTimeout(() => {
                                    const password =
                                        document.getElementById('password');
                                    if (password) {
                                        password.scrollIntoView({
                                            behavior: 'smooth',
                                        });
                                    }
                                }, 10);
                            }
                        }}
                    />
                </FormControl>
                <FormControl>
                    <InputBox
                        value={state.password}
                        type="password"
                        id="password"
                        label="Password"
                        handleChange={handleChange}
                        placeholder="Password"
                        canTogglePasswordReadability={true}
                        testId="password"
                    />
                </FormControl>
                <div className="auth-note">
                    <div
                        data-cy="forgot-pass-route"
                        className="a-div"
                        onClick={() => navigate('/password/forgot')}
                    >
                        {t('Forgot password?')}
                    </div>
                </div>
                <FormControl>
                    <Button
                        classes={!isSubmittable() ? 'disabled' : ''}
                        isDisabled={!isSubmittable()}
                        isLoading={isLoading}
                        id="login-btn"
                    >
                        {t('Login')}
                    </Button>
                </FormControl>
            </form>
        </>
    );
};

export default LoginSection;
